import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { determineUserBadges } from '../../utils/badgeUtils';

const MemberProfile = ({ isLoggedIn }) => {
  const { id } = useParams();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('about'); // 'about', 'groups', 'events', 'network'
  const [isInNetwork, setIsInNetwork] = useState(false); // Track if member is in user's network

  // Mock data for a specific member
  useEffect(() => {
    // In a real app, this would be an API call
    // Create different member data based on ID to match member directory
    const memberData = {
      1: { firstName: 'John', lastName: 'Smith', title: 'Investment Banking Associate', company: 'Goldman Sachs', location: 'New York, NY' },
      2: { firstName: 'Sarah', lastName: 'Johnson', title: 'Private Equity Analyst', company: 'Blackstone', location: 'New York, NY' },
      3: { firstName: 'Michael', lastName: 'Chen', title: 'VP of Corporate Development', company: 'Morgan Stanley', location: 'Chicago, IL' },
      4: { firstName: 'Emily', lastName: 'Rodriguez', title: 'Tax Manager', company: 'Deloitte', location: 'Los Angeles, CA' },
      5: { firstName: 'David', lastName: 'Kim', title: 'Associate Attorney', company: 'Skadden', location: 'Boston, MA' },
      6: { firstName: 'Jessica', lastName: 'Lee', title: 'Investment Banking Analyst', company: 'JP Morgan', location: 'San Francisco, CA' },
      7: { firstName: 'Robert', lastName: 'Wilson', title: 'Director', company: 'KPMG', location: 'Dallas, TX' },
      8: { firstName: 'Amanda', lastName: 'Thompson', title: 'Wealth Management Associate', company: 'UBS', location: 'Miami, FL' }
    };

    const currentMember = memberData[parseInt(id)] || memberData[2]; // Default to Sarah Johnson

    const mockMember = {
      id: parseInt(id),
      firstName: currentMember.firstName,
      lastName: currentMember.lastName,
      title: currentMember.title,
      company: currentMember.company,
      location: currentMember.location,
      email: `${currentMember.firstName.toLowerCase()}.${currentMember.lastName.toLowerCase()}@example.com`,
      phone: '(212) 555-1234',
      linkedin: `https://www.linkedin.com/in/${currentMember.firstName.toLowerCase()}${currentMember.lastName.toLowerCase()}`,
      bio: `${currentMember.firstName} is a ${currentMember.title} at ${currentMember.company} with extensive experience in finance and M&A transactions. They are an active member of the APN community.`,
      education: [
        {
          institution: 'Harvard Business School',
          degree: 'MBA, Finance',
          year: '2012'
        },
        {
          institution: 'University of Pennsylvania',
          degree: 'BS, Economics',
          year: '2008'
        }
      ],
      experience: [
        {
          company: 'Morgan Stanley',
          title: 'VP of Investment Banking',
          startDate: '2018',
          endDate: 'Present',
          description: 'Lead M&A transactions for technology and healthcare clients.'
        },
        {
          company: 'Goldman Sachs',
          title: 'Associate',
          startDate: '2012',
          endDate: '2018',
          description: 'Worked on capital markets transactions and financial advisory.'
        }
      ],
      groups: [
        {
          id: 1,
          name: 'Investment Banking Professionals',
          role: 'Admin'
        },
        {
          id: 5,
          name: 'Women in Finance',
          role: 'Member'
        }
      ],
      events: [
        {
          id: 3,
          title: 'APN New York Networking Event',
          date: 'March 15, 2023',
          status: 'Attended'
        },
        {
          id: 5,
          title: 'APN Chicago Conference',
          date: 'June 10, 2023',
          status: 'Registered'
        }
      ],
      profileImage: '/apn-logos/caroline-header-photo.jpeg',
      memberSince: '2022-05-15',
      connections: [
        {
          id: 1,
          name: 'John Smith',
          title: 'Managing Director',
          company: 'Goldman Sachs',
          image: '/images/profiles/john-smith.jpg',
          mutualConnections: 12
        },
        {
          id: 3,
          name: 'Michael Chen',
          title: 'Director',
          company: 'JPMorgan Chase',
          image: '/images/profiles/michael-chen.jpg',
          mutualConnections: 8
        },
        {
          id: 4,
          name: 'Jessica Williams',
          title: 'Director of Private Equity',
          company: 'KKR',
          image: '/images/profiles/jessica-williams.jpg',
          mutualConnections: 5
        }
      ],
      suggestedConnections: [
        {
          id: 6,
          name: 'David Lee',
          title: 'VP of Corporate Development',
          company: 'Citigroup',
          image: '/images/profiles/default-avatar.png',
          mutualConnections: 3
        },
        {
          id: 7,
          name: 'Amanda Rodriguez',
          title: 'Investment Banking Associate',
          company: 'Bank of America',
          image: '/images/profiles/default-avatar.png',
          mutualConnections: 2
        }
      ],
      // Badge properties for calculation
      isFoundingMember: parseInt(id) === 1 || parseInt(id) === 5,
      isVipMember: parseInt(id) === 2 || parseInt(id) === 6,
      memberSince: parseInt(id) === 3 ? 'May 2018' : parseInt(id) === 7 ? 'January 2015' : parseInt(id) === 1 ? 'March 2019' : 'June 2023',
      role: parseInt(id) === 4 ? 'Chapter President' : '',
      chapter: 'New York',
      referralCount: parseInt(id) === 8 ? 7 : parseInt(id) === 3 ? 12 : parseInt(id) === 2 ? 5 : 0,
      ambassadorSince: parseInt(id) === 8 ? 'January 2024' : parseInt(id) === 3 ? 'June 2020' : null
    };

    // Calculate badges for the member
    mockMember.badges = determineUserBadges(mockMember);

    // Simulate API call
    setTimeout(() => {
      setMember(mockMember);

      // Check if member is in user's network (mock)
      // In a real app, this would come from the API
      setIsInNetwork(parseInt(id) === 2 || parseInt(id) === 5);

      setLoading(false);
    }, 500);
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-apn-blue"></div>
      </div>
    );
  }

  if (!member) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Member Not Found</h1>
          <p className="text-gray-600 mb-8">The member you're looking for doesn't exist or has been removed.</p>
          <Link to="/directory" className="inline-block bg-apn-blue text-white py-2 px-6 rounded hover:bg-blue-700 transition duration-200">
            Back to Directory
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Page Header */}
      <div className="bg-apn-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* APN Logo */}
            <div className="flex justify-center mb-4">
              <img
                src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-white.jpg`}
                alt="APN Logo"
                className="h-16"
              />
            </div>
            <h1 className="text-3xl font-bold">Member Profile</h1>
          </div>
        </div>
      </div>

      {/* Profile Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            {/* Profile Header */}
            <div className="p-6 bg-gray-50 border-b border-gray-200">
              <div className="flex flex-col md:flex-row items-center md:items-start">
                <div className="mb-4 md:mb-0 md:mr-6">
                  <div className="relative">
                    <img
                      src={`${process.env.PUBLIC_URL}${member.profileImage}`}
                      alt={`${member.firstName} ${member.lastName}`}
                      className="h-32 w-32 rounded-full object-cover border-4 border-white shadow"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = `${process.env.PUBLIC_URL}/images/profiles/default-avatar.png`;
                      }}
                    />
                    {member.badges && member.badges.length > 0 && (
                      <div className="absolute -bottom-2 -right-2 flex">
                        {member.badges.slice(0, 2).map((badge, index) => (
                          <div
                            key={badge.id}
                            className="h-8 w-8 rounded-full bg-white shadow-md border border-gray-200 overflow-hidden"
                            style={{ marginLeft: index > 0 ? '-0.5rem' : 0 }}
                            title={badge.name}
                          >
                            <img
                              src={`${process.env.PUBLIC_URL}${badge.image}`}
                              alt={badge.name}
                              className="h-full w-full object-contain"
                            />
                          </div>
                        ))}
                        {member.badges.length > 2 && (
                          <div className="h-8 w-8 rounded-full bg-gray-200 shadow-md border border-gray-200 flex items-center justify-center text-xs font-semibold text-gray-700" style={{ marginLeft: '-0.5rem' }}>
                            +{member.badges.length - 2}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-center md:text-left flex-grow">
                  <h2 className="text-2xl font-bold text-gray-900 flex items-center justify-center md:justify-start">
                    {member.firstName} {member.lastName}
                    {member.badges && member.badges.length > 0 && (
                      <div className="ml-2 flex">
                        {member.badges.slice(0, 2).map((badge) => (
                          <div
                            key={badge.id}
                            className="h-6 w-6 rounded-full bg-white shadow-sm border border-gray-200 overflow-hidden ml-1"
                            title={badge.name}
                          >
                            <img
                              src={`${process.env.PUBLIC_URL}${badge.image}`}
                              alt={badge.name}
                              className="h-full w-full object-contain"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </h2>
                  <p className="text-lg text-gray-600">{member.title} at {member.company}</p>
                  <p className="text-gray-500 mb-4">{member.location}</p>
                  <div className="flex flex-wrap justify-center md:justify-start gap-2">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      Member since {new Date(member.memberSince).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
                    </span>
                    {member.groups && member.groups.length > 0 && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {member.groups.length} Groups
                      </span>
                    )}
                    {member.events && member.events.length > 0 && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        {member.events.length} Events
                      </span>
                    )}
                  </div>
                </div>
                <div className="mt-4 md:mt-0">
                  {isLoggedIn && (
                    <div className="flex flex-col space-y-2">
                      <a
                        href={`mailto:${member.email}`}
                        className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-apn-blue hover:bg-blue-700"
                      >
                        <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        Contact
                      </a>
                      {isInNetwork ? (
                        <button
                          onClick={() => setIsInNetwork(false)}
                          className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                        >
                          <svg className="h-4 w-4 mr-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7a4 4 0 11-8 0 4 4 0 018 0zM9 14a6 6 0 00-6 6v1h12v-1a6 6 0 00-6-6zM21 12h-6" />
                          </svg>
                          Remove from Network
                        </button>
                      ) : (
                        <button
                          onClick={() => setIsInNetwork(true)}
                          className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                        >
                          <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                          </svg>
                          Add to Network
                        </button>
                      )}
                      {member.linkedin && (
                        <a
                          href={member.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                        >
                          <svg className="h-4 w-4 mr-2 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                          </svg>
                          LinkedIn
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Profile Tabs */}
            <div className="border-b border-gray-200">
              <nav className="flex -mb-px">
                <button
                  onClick={() => setActiveTab('about')}
                  className={`py-4 px-6 font-medium text-sm border-b-2 ${
                    activeTab === 'about'
                      ? 'border-apn-blue text-apn-blue'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  About
                </button>
                <button
                  onClick={() => setActiveTab('groups')}
                  className={`py-4 px-6 font-medium text-sm border-b-2 ${
                    activeTab === 'groups'
                      ? 'border-apn-blue text-apn-blue'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Groups
                </button>
                <button
                  onClick={() => setActiveTab('events')}
                  className={`py-4 px-6 font-medium text-sm border-b-2 ${
                    activeTab === 'events'
                      ? 'border-apn-blue text-apn-blue'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Events
                </button>
                <button
                  onClick={() => setActiveTab('network')}
                  className={`py-4 px-6 font-medium text-sm border-b-2 ${
                    activeTab === 'network'
                      ? 'border-apn-blue text-apn-blue'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Network
                </button>
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {/* About Tab */}
              {activeTab === 'about' && (
                <div className="space-y-8">
                  {/* Bio Section */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Bio</h3>
                    <p className="text-gray-700">{member.bio}</p>
                  </div>

                  {/* Experience Section */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Experience</h3>
                    <div className="space-y-4">
                      {member.experience.map((exp, index) => (
                        <div key={index} className="border-l-2 border-gray-200 pl-4 py-1">
                          <h4 className="font-semibold text-gray-900">{exp.title}</h4>
                          <p className="text-gray-600">{exp.company}</p>
                          <p className="text-sm text-gray-500">{exp.startDate} - {exp.endDate}</p>
                          <p className="text-gray-700 mt-1">{exp.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Education Section */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Education</h3>
                    <div className="space-y-4">
                      {member.education.map((edu, index) => (
                        <div key={index} className="border-l-2 border-gray-200 pl-4 py-1">
                          <h4 className="font-semibold text-gray-900">{edu.institution}</h4>
                          <p className="text-gray-600">{edu.degree}</p>
                          <p className="text-sm text-gray-500">{edu.year}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Contact Information (only visible to logged-in members) */}
                  {isLoggedIn && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Contact Information</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-500">Email</p>
                          <p className="text-gray-700">{member.email}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Phone</p>
                          <p className="text-gray-700">{member.phone}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">LinkedIn</p>
                          <a
                            href={member.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-apn-blue hover:underline"
                          >
                            {member.linkedin.replace('https://www.linkedin.com/in/', '')}
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Groups Tab */}
              {activeTab === 'groups' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Groups</h3>
                  {member.groups && member.groups.length > 0 ? (
                    <div className="space-y-4">
                      {member.groups.map((group) => (
                        <div key={group.id} className="border border-gray-200 rounded-lg p-4 flex justify-between items-center">
                          <div>
                            <h4 className="font-semibold text-gray-900">{group.name}</h4>
                            <p className="text-sm text-gray-500">Role: {group.role}</p>
                          </div>
                          <Link
                            to={`/groups/${group.id}`}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-apn-blue hover:bg-blue-700 focus:outline-none focus:border-blue-700 focus:shadow-outline-blue active:bg-blue-800 transition ease-in-out duration-150"
                          >
                            View Group
                          </Link>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">This member hasn't joined any groups yet.</p>
                  )}
                </div>
              )}

              {/* Events Tab */}
              {activeTab === 'events' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Events</h3>
                  {member.events && member.events.length > 0 ? (
                    <div className="space-y-4">
                      {member.events.map((event) => (
                        <div key={event.id} className="border border-gray-200 rounded-lg p-4 flex justify-between items-center">
                          <div>
                            <h4 className="font-semibold text-gray-900">{event.title}</h4>
                            <p className="text-sm text-gray-500">{event.date}</p>
                            <span className={`inline-block mt-1 px-2 py-1 text-xs rounded-full ${
                              event.status === 'Attended'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}>
                              {event.status}
                            </span>
                          </div>
                          <Link
                            to={`/events/${event.id}`}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-apn-blue hover:bg-blue-700 focus:outline-none focus:border-blue-700 focus:shadow-outline-blue active:bg-blue-800 transition ease-in-out duration-150"
                          >
                            View Event
                          </Link>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">This member hasn't attended any events yet.</p>
                  )}
                </div>
              )}

              {/* Network Tab */}
              {activeTab === 'network' && (
                <div>
                  {/* Current Connections */}
                  <div className="mb-8">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Connections ({member.connections.length})</h3>
                      {isInNetwork && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <svg className="-ml-0.5 mr-1.5 h-2 w-2 text-green-400" fill="currentColor" viewBox="0 0 8 8">
                            <circle cx="4" cy="4" r="3" />
                          </svg>
                          In Your Network
                        </span>
                      )}
                    </div>

                    {member.connections && member.connections.length > 0 ? (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Show only first 6 connections */}
                          {member.connections.slice(0, 6).map((connection) => (
                            <div key={connection.id} className="border border-gray-200 rounded-lg p-4 flex items-center">
                              <div className="flex-shrink-0 mr-4">
                                <img
                                  src={`${process.env.PUBLIC_URL}${connection.image}`}
                                  alt={connection.name}
                                  className="h-12 w-12 rounded-full object-cover"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = `${process.env.PUBLIC_URL}/images/profiles/default-avatar.png`;
                                  }}
                                />
                              </div>
                              <div className="flex-grow">
                                <h4 className="font-semibold text-gray-900">{connection.name}</h4>
                                <p className="text-sm text-gray-600">{connection.title} at {connection.company}</p>
                                <p className="text-xs text-gray-500 mt-1">{connection.mutualConnections} mutual connections</p>
                              </div>
                              <div>
                                <Link
                                  to={`/directory/${connection.id}`}
                                  className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm leading-5 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-100 transition ease-in-out duration-150"
                                >
                                  View
                                </Link>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Show View All button if more than 6 connections */}
                        {member.connections.length > 6 && (
                          <div className="mt-6 text-center">
                            <span className="text-gray-500 text-sm">Showing 6 of {member.connections.length} connections</span>
                            <button
                              className="block mx-auto mt-2 px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-apn-blue"
                              onClick={() => window.open(`/directory/${member.id}/connections`, '_blank')}
                            >
                              View All Connections
                            </button>
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-gray-500 text-center py-8">This member doesn't have any connections yet.</p>
                    )}
                  </div>

                  {/* Mutual Connections */}
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Mutual Connections</h3>
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center">
                        <div className="flex -space-x-2 mr-4">
                          {/* Show avatars of first 3 mutual connections */}
                          <img className="h-8 w-8 rounded-full ring-2 ring-white" src={`${process.env.PUBLIC_URL}/images/profiles/default-avatar.png`} alt="" />
                          <img className="h-8 w-8 rounded-full ring-2 ring-white" src={`${process.env.PUBLIC_URL}/images/profiles/default-avatar.png`} alt="" />
                          <img className="h-8 w-8 rounded-full ring-2 ring-white" src={`${process.env.PUBLIC_URL}/images/profiles/default-avatar.png`} alt="" />
                        </div>
                        <div>
                          <p className="text-gray-700">You and {member.firstName} have <span className="font-semibold">5 mutual connections</span></p>
                          <button className="text-apn-blue hover:text-blue-700 text-sm font-medium mt-1">View mutual connections</button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Suggested Connections */}
                  {member.suggestedConnections && member.suggestedConnections.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">People You May Know</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {member.suggestedConnections.map((connection) => (
                          <div key={connection.id} className="border border-gray-200 rounded-lg p-4 flex items-center bg-gray-50">
                            <div className="flex-shrink-0 mr-4">
                              <img
                                src={`${process.env.PUBLIC_URL}${connection.image}`}
                                alt={connection.name}
                                className="h-12 w-12 rounded-full object-cover"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = `${process.env.PUBLIC_URL}/images/profiles/default-avatar.png`;
                                }}
                              />
                            </div>
                            <div className="flex-grow">
                              <h4 className="font-semibold text-gray-900">{connection.name}</h4>
                              <p className="text-sm text-gray-600">{connection.title} at {connection.company}</p>
                              <p className="text-xs text-gray-500 mt-1">{connection.mutualConnections} mutual connections</p>
                            </div>
                            <div>
                              <button
                                className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:border-green-700 focus:shadow-outline-green active:bg-green-800 transition ease-in-out duration-150"
                              >
                                <svg className="-ml-0.5 mr-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                                </svg>
                                Connect
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="bg-apn-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* APN Logo */}
          <div className="flex justify-center mb-6">
            <img
              src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-SQ-white.jpg`}
              alt="APN Logo Square"
              className="h-20"
            />
          </div>
          <h2 className="text-3xl font-bold mb-4">Ready to Join the Network?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Connect with finance professionals, access exclusive resources, and accelerate your career with APN.
          </p>
          <Link to="/register" className="btn-primary bg-white text-apn-blue hover:bg-gray-100">
            Become a Member Today
          </Link>
        </div>
      </section>
    </div>
  );
};

export default MemberProfile;
